import { useMediaQuery } from '@mui/material'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { map } from 'lodash'
import { Fragment, useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useLocation, useSearchParams } from 'react-router-dom'
import { ThreadShortDTO } from '../../api/gen/base'
import { useInfiniteThreadListQuery } from '../../api/hooks/useInfiniteThreadListQuery'
import { REQUESTS_ROUTE } from '../../constants/routing.constants'
import { DateChip } from '../../pages/InternalPage/RequestMessagesPage/components/DateChip'
import { RequestItem } from '../../pages/InternalPage/RequestsPage/components/RequestItem'
import { useWrapperStore } from '../../store/storeWrapper'
import { theme } from '../../theme'
import { RequestInput } from '../RequestInput'

export const Request = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [searchParams, setSearchParams] = useSearchParams()
    const searchParamIsArchived = searchParams.get('is_archived') === 'true'
    const { pathname } = useLocation()

    const [tab, setTab] = useState(Number(searchParamIsArchived))
    const {
        currentAgentNameInDialog,
        currentAssistantIdDialog,
        fetchModelDetail,
        setOpenSidebarType,
    } = useWrapperStore((state) => state)

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isFetchedAfterMount,
    } = useInfiniteThreadListQuery({
        is_archived: Boolean(tab),
        workplace_id: searchParams.get('workplaceId'),
    })

    const requestsList = useMemo(
        () =>
            data?.pages.reduce<ThreadShortDTO[]>((acc, page) => acc.concat(page.objects), []) ?? [],
        [data],
    )

    const [infiniteRef] = useInfiniteScroll({
        loading: isFetching,
        hasNextPage,
        onLoadMore: fetchNextPage,
    })

    useEffect(() => {
        setTab(Number(searchParamIsArchived))
    }, [searchParamIsArchived])

    useEffect(() => {
        if (currentAgentNameInDialog) {
            fetchModelDetail(currentAgentNameInDialog, currentAssistantIdDialog)
        }
    }, [currentAgentNameInDialog, currentAssistantIdDialog])

    const handleClickTab = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue)
        if (newValue === 0) {
            searchParams.set('is_archived', 'false')
        }
        if (newValue === 1) {
            setOpenSidebarType(null)
            searchParams.set('is_archived', 'true')
        }
        setSearchParams(searchParams)
    }

    const { projectListActive } = useWrapperStore((state) => state)

    return (
        <Stack sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
            {pathname === REQUESTS_ROUTE ? (
                <Tabs
                    value={tab}
                    onChange={handleClickTab}
                    sx={{
                        minHeight: '48px',
                        borderBottom: 'none',
                        p: 0,
                    }}>
                    <Tab
                        variant="secondary"
                        disabled={isFetching}
                        label={
                            <Stack spacing={1} direction="row">
                                <Typography>Все</Typography>
                                {tab === 0 && (
                                    <Typography
                                        sx={{
                                            background: '#fff',
                                            borderRadius: '50px',
                                            height: '22px',
                                            width: '20px',
                                            fontSize: '12px',
                                        }}>
                                        {requestsList?.length}
                                    </Typography>
                                )}
                            </Stack>
                        }
                        id="0"
                    />
                    <Tab
                        variant="secondary"
                        disabled={isFetching}
                        label={
                            <Stack spacing={1} direction="row">
                                <Typography>Архивированные</Typography>
                                {tab === 1 && (
                                    <Typography
                                        sx={{
                                            background: '#fff',
                                            borderRadius: '50px',
                                            height: '22px',
                                            width: '20px',
                                            fontSize: '12px',
                                        }}>
                                        {requestsList?.length}
                                    </Typography>
                                )}
                            </Stack>
                        }
                        id="1"
                    />
                </Tabs>
            ) : null}

            {isFetching && !isFetchedAfterMount && !isFetchingNextPage ? (
                <>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={60}
                        sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                    />
                    <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={60}
                        sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                    />
                    <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={60}
                        sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                    />
                    <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={60}
                        sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                    />
                    <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={60}
                        sx={{ mb: '4px', py: '12px', borderRadius: '24px' }}
                    />
                    <Divider sx={{ color: '#F4F4F6', mb: 1 }} />
                </>
            ) : (
                <>
                    {requestsList.length > 0 && (
                        <Stack
                            sx={{
                                width: '100%',
                                height: '100%',
                                overflow: 'auto',
                                flexGrow: 1,
                                flexBasis: 0,
                            }}>
                            {map(requestsList, (request, index) => (
                                <Fragment key={request.id}>
                                    <DateChip
                                        createdAt={request.created_at}
                                        createdAtLast={
                                            index ? requestsList[index - 1]?.created_at : undefined
                                        }
                                    />
                                    <RequestItem request={request} />
                                </Fragment>
                            ))}
                            {hasNextPage && (
                                <Skeleton
                                    ref={infiniteRef}
                                    variant="rectangular"
                                    width="100%"
                                    height={60}
                                    sx={{
                                        mb: '4px',
                                        py: '12px',
                                        borderRadius: '24px',
                                        minHeight: '60px',
                                    }}
                                />
                            )}
                        </Stack>
                    )}
                    {requestsList.length === 0 && (
                        <Typography
                            variant="h5"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                textAlign: 'center',
                                height: `100%`,
                                justifyContent: 'center',
                            }}>
                            Запросов не найдено
                        </Typography>
                    )}
                    {projectListActive && tab === 0 && !isMobile && <RequestInput />}
                </>
            )}
        </Stack>
    )
}
